import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Tooltip,
} from "recharts";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import theme from "@/theme";
import { convertPxToRem } from "@/utils";
import {
  themeColors,
  backgroundColors,
  CustomDot,
  CustomActiveDot,
  CustomTooltipTracker,
  TrendChartSummarySection,
} from "../../HealthChart/components";
import {
  useChartSelection,
  useTrendChartCompactViewport,
} from "../../HealthChart/hooks";
import {
  calculateDynamicYDomain,
  getChartFocusStyles,
  getTrendChartLayoutZoneLabels,
  trendGraphCardHorizontalPaddingSx,
  trendGraphCardPaperSx,
} from "../../HealthChart/utils";
import { ChartDataPoint } from "../../HealthChart/types";

export type MentalWellbeingChartDataPoint = ChartDataPoint & {
  score: number;
};

export interface MentalWellbeingChartProps {
  depressionChartData: MentalWellbeingChartDataPoint[];
  anxietyChartData: MentalWellbeingChartDataPoint[];
}

type ViewMode = "depression" | "anxiety";

export const getWellbeingScoreColor = (score: number): string => {
  if (score === 0) {
    return themeColors.success;
  }
  if (score >= 1 && score <= 2) {
    return themeColors.warning;
  }
  return themeColors.error;
};

const CHART_HEIGHT = 200;
const Y_AXIS_WIDTH_FULL = 27;
const Y_MARGIN = { top: 10, bottom: 10 };
const CHART_BOTTOM_MARGIN = 20;
const DEFAULT_Y_DOMAIN: [number, number] = [-1, 6];
const Y_TICKS = [0, 1, 3, 6];

const ZONE_LABELS: {
  yMin: number;
  yMax: number;
  labelKey:
    | "HealthChart.mentalWellbeingChart.zone.lowerRisk"
    | "HealthChart.mentalWellbeingChart.zone.higherRisk"
    | "HealthChart.mentalWellbeingChart.zone.highRisk";
}[] = [
  {
    yMin: 1,
    yMax: 2,
    labelKey: "HealthChart.mentalWellbeingChart.zone.lowerRisk",
  },
  {
    yMin: 1,
    yMax: 4,
    labelKey: "HealthChart.mentalWellbeingChart.zone.higherRisk",
  },
  {
    yMin: 3,
    yMax: 6,
    labelKey: "HealthChart.mentalWellbeingChart.zone.highRisk",
  },
];

const MentalWellbeingChart: React.FC<MentalWellbeingChartProps> = ({
  depressionChartData,
  anxietyChartData,
}) => {
  const { t } = useTranslation();
  const isCompactChart = useTrendChartCompactViewport();
  const {
    left: chartLeftMargin,
    right: chartRightMargin,
    yAxisWidth,
  } = getTrendChartLayoutZoneLabels(isCompactChart, Y_AXIS_WIDTH_FULL);
  const [viewMode, setViewMode] = useState<ViewMode>("depression");

  const data =
    viewMode === "depression" ? depressionChartData : anxietyChartData;

  const yDomain = useMemo(
    () =>
      calculateDynamicYDomain(
        data.map((d) => d.score),
        DEFAULT_Y_DOMAIN
      ),
    [data]
  );
  const yRange = yDomain[1] - yDomain[0];

  const {
    setHoveredPoint,
    hasAnimated,
    handlePointClick,
    activePoint,
    activeIndex,
  } = useChartSelection(data);

  const activeScore = activePoint?.score;

  const handleViewModeChange = (mode: ViewMode) => {
    hasAnimated.current = true;
    setViewMode(mode);
  };

  return (
    <>
      <style>{getChartFocusStyles("mental-wellbeing-chart")}</style>
      <Box className="mental-wellbeing-chart" sx={trendGraphCardPaperSx}>
        <Stack spacing={2} sx={trendGraphCardHorizontalPaddingSx}>
          <Typography
            variant="h4"
            sx={{
              fontSize: 16,
            }}
          >
            {t("HealthChart.mentalWellbeingChart.title")}
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            {(["depression", "anxiety"] as ViewMode[]).map((mode) => (
              <Button
                key={mode}
                fullWidth
                onClick={() => handleViewModeChange(mode)}
                variant={viewMode === mode ? "contained" : "outlined"}
                sx={{
                  paddingTop: convertPxToRem(6),
                  paddingBottom: convertPxToRem(6),
                  paddingLeft: convertPxToRem(10),
                  paddingRight: convertPxToRem(10),
                  background:
                    viewMode === mode
                      ? themeColors.primary
                      : theme.palette.primary[100],
                  color:
                    viewMode === mode ? "white" : theme.palette.primary.main,
                  ...(viewMode !== mode && {
                    borderColor: theme.palette.primary.main,
                  }),
                }}
              >
                {t(`HealthChart.mentalWellbeingChart.view.${mode}`)}
              </Button>
            ))}
          </Box>

          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: CHART_HEIGHT,
              outline: "none",
            }}
          >
            <ResponsiveContainer>
              <LineChart
                data={data}
                margin={{
                  top: 10,
                  right: chartRightMargin,
                  left: chartLeftMargin,
                  bottom: CHART_BOTTOM_MARGIN,
                }}
                onClick={handlePointClick}
              >
                <CartesianGrid
                  stroke={theme.palette.grey[300]}
                  strokeDasharray="5 5"
                  vertical={false}
                  horizontal
                />

                <ReferenceArea
                  y1={3}
                  y2={6}
                  fill={backgroundColors.errorLight}
                  fillOpacity={0.5}
                />
                <ReferenceArea
                  y1={1}
                  y2={3}
                  fill={backgroundColors.warningLight}
                  fillOpacity={0.5}
                />
                <ReferenceArea
                  y1={0}
                  y2={1}
                  fill={backgroundColors.successLight}
                  fillOpacity={0.5}
                />

                <ReferenceLine
                  y={3}
                  stroke={themeColors.error}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
                <ReferenceLine
                  y={1}
                  stroke={themeColors.warning}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
                <ReferenceLine
                  y={0}
                  stroke={themeColors.success}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />

                <XAxis
                  dataKey="date"
                  tick={{
                    fontSize: 10,
                    fontWeight: 700,
                    fill: theme.palette.grey[700],
                  }}
                  axisLine={{ stroke: theme.palette.grey[500] }}
                  tickLine={false}
                  dy={5}
                />

                <YAxis
                  domain={yDomain}
                  ticks={Y_TICKS}
                  tick={{
                    fontSize: 11,
                    fontWeight: 700,
                    fill: theme.palette.grey[700],
                  }}
                  axisLine={{ stroke: theme.palette.grey[500] }}
                  tickLine={false}
                  width={yAxisWidth}
                />

                <Tooltip
                  content={(props) => (
                    <CustomTooltipTracker
                      {...props}
                      setHoveredPoint={setHoveredPoint}
                    />
                  )}
                  cursor={{
                    stroke: theme.palette.grey[900],
                    strokeWidth: 1,
                    strokeDasharray: "4 4",
                  }}
                />

                <Line
                  type="linear"
                  dataKey="score"
                  stroke={theme.palette.grey[300]}
                  strokeWidth={2}
                  dot={(props: any) => (
                    <CustomDot
                      {...props}
                      dataKey="score"
                      getColorFn={getWellbeingScoreColor}
                    />
                  )}
                  activeDot={(props: any) => (
                    <CustomActiveDot
                      {...props}
                      dataKey="score"
                      getColorFn={getWellbeingScoreColor}
                    />
                  )}
                  isAnimationActive={hasAnimated.current}
                  animationDuration={800}
                  animationEasing="ease-in-out"
                />
              </LineChart>
            </ResponsiveContainer>

            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `calc(100% - ${chartRightMargin}px)`,
                width: chartRightMargin,
                pointerEvents: "none",
                zIndex: 1,
              }}
            >
              {ZONE_LABELS.map(({ yMin, yMax, labelKey }) => {
                const plotHeight =
                  CHART_HEIGHT - Y_MARGIN.top - CHART_BOTTOM_MARGIN;
                const zoneCenterY = (yMin + yMax) / 2;
                const yToTop =
                  Y_MARGIN.top +
                  plotHeight * (1 - (zoneCenterY - yDomain[0]) / yRange);
                const color =
                  labelKey === "HealthChart.mentalWellbeingChart.zone.lowerRisk"
                    ? themeColors.success
                    : labelKey ===
                        "HealthChart.mentalWellbeingChart.zone.higherRisk"
                      ? themeColors.warning
                      : themeColors.error;
                return (
                  <Box
                    key={labelKey}
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      top: yToTop - 6,
                      height: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t(labelKey)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <TrendChartSummarySection
            activeIndex={activeIndex}
            dataLength={data.length}
            chevronLeftMargin={chartLeftMargin}
            chevronRightMargin={chartRightMargin}
            datePrefix={t("HealthChart.mentalWellbeingChart.datePrefix")}
            fullDate={activePoint?.fullDate ?? ""}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span
                style={{
                  fontSize: convertPxToRem(24),
                  fontWeight: 800,
                  color: theme.palette.grey[700],
                }}
              >
                {activeScore}
              </span>
              <span
                style={{
                  fontSize: convertPxToRem(14),
                  color: theme.palette.grey[900],
                  marginLeft: convertPxToRem(4),
                }}
              >
                {t("HealthChart.mentalWellbeingChart.unit")}
              </span>
            </div>
          </TrendChartSummarySection>
        </Stack>
      </Box>
    </>
  );
};

export default MentalWellbeingChart;
